import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { getAccountsAll, createAccount, updateAccount, deleteAccount } from '../utils/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BanknotesIcon,
  MagnifyingGlassIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const ACCOUNT_TYPES = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

const ChartOfAccounts = () => {
  usePageTitle('Chart of Accounts');

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [viewingAccount, setViewingAccount] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    account_number: '',
    name: '',
    account_type: '',
    description: '',
    parent_account_id: '',
    is_active: true,
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const response = await getAccountsAll();
      setAccounts(response.data);
    } catch (error) {
      toast.error('Error loading accounts');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        account_number: account.account_number || '',
        name: account.name || '',
        account_type: account.account_type || '',
        description: account.description || '',
        parent_account_id: account.parent_account_id || '',
        is_active: Boolean(account.is_active),
      });
    } else {
      setEditingAccount(null);
      setFormData({
        account_number: '',
        name: '',
        account_type: '',
        description: '',
        parent_account_id: '',
        is_active: true,
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingAccount(null);
  };

  const openViewModal = (account) => {
    setViewingAccount(account);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewingAccount(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.account_number || !formData.name || !formData.account_type) {
      toast.error('Account number, name, and type are required');
      return;
    }

    if (formData.description && formData.description.length > 500) {
      toast.error('Description must be 500 characters or less');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        description: formData.description || null,
        parent_account_id: formData.parent_account_id || null,
      };

      if (editingAccount) {
        await updateAccount(editingAccount.id, submitData);
        toast.success('Account updated successfully');
      } else {
        await createAccount(submitData);
        toast.success('Account created successfully');
      }
      closeModal();
      loadAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving account');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Are you sure you want to delete "${account.name}" (${account.account_number})?`)) {
      return;
    }

    try {
      await deleteAccount(account.id);
      toast.success('Account deleted successfully');
      loadAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting account');
    }
  };

  // Get available parent accounts (excluding the current one when editing)
  const getAvailableParents = () => {
    if (editingAccount) {
      return accounts.filter(acc => acc.id !== editingAccount.id);
    }
    return accounts;
  };

  // Calculate depth for each account (how many levels deep in hierarchy)
  const calculateDepth = (accountId, depth = 0) => {
    const account = accounts.find(acc => acc.id === accountId);
    if (!account || !account.parent_account_id) {
      return depth;
    }
    return calculateDepth(account.parent_account_id, depth + 1);
  };

  // Build hierarchical list with proper ordering
  const buildHierarchicalList = (accountsList) => {
    const accountsWithDepth = accountsList.map(acc => ({
      ...acc,
      depth: calculateDepth(acc.id)
    }));

    // Sort by account number to maintain order
    accountsWithDepth.sort((a, b) => {
      // If one is a parent of another, parent comes first
      if (a.id === b.parent_account_id) return -1;
      if (b.id === a.parent_account_id) return 1;

      // Otherwise sort by account number
      return a.account_number.localeCompare(b.account_number);
    });

    return accountsWithDepth;
  };

  // Filter accounts based on search query
  const filteredAccountsRaw = accounts.filter((acc) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      acc.account_number?.toLowerCase().includes(searchLower) ||
      acc.name?.toLowerCase().includes(searchLower) ||
      acc.account_type?.toLowerCase().includes(searchLower) ||
      acc.parent_account_number?.toLowerCase().includes(searchLower) ||
      acc.parent_account_name?.toLowerCase().includes(searchLower)
    );
  });

  const filteredAccounts = buildHierarchicalList(filteredAccountsRaw);

  if (loading) {
    return <div className="loading-overlay"><div className="spinner" /><span>Loading accounts...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Chart of Accounts</h1>
          <p className="page-subtitle">Manage general ledger accounts</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <PlusIcon style={{ width: 18, height: 18 }} />
          Add Account
        </button>
      </div>

      <div className="card">
        {accounts.length > 0 && (
          <div className="search-bar">
            <div className="search-input-wrapper">
              <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
              <input
                type="text"
                className="form-input search-input"
                placeholder="Search by account number, name, type, or parent..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        )}

        {filteredAccounts.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Account Number</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Parent Account</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <tr key={account.id}>
                    <td className="font-mono"><strong>{account.account_number}</strong></td>
                    <td style={{ paddingLeft: `calc(var(--space-md) + ${account.depth * 2}rem)` }}>
                      {account.depth > 0 && (
                        <span style={{ color: 'var(--color-text-tertiary)', marginRight: 'var(--space-sm)' }}>
                          {'└─ '}
                        </span>
                      )}
                      {account.name}
                    </td>
                    <td>
                      <span className={`badge badge-${account.account_type?.toLowerCase()}`}>
                        {account.account_type}
                      </span>
                    </td>
                    <td>
                      {account.parent_account_number ? (
                        <span className="font-mono">
                          {account.parent_account_number} - {account.parent_account_name}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {account.is_active ? (
                        <span className="badge badge-approved">Active</span>
                      ) : (
                        <span className="badge badge-draft">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => openViewModal(account)}
                          title="View Details"
                        >
                          <EyeIcon style={{ width: 16, height: 16 }} />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => openModal(account)}
                          title="Edit"
                        >
                          <PencilIcon style={{ width: 16, height: 16 }} />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => handleDelete(account)}
                          title="Delete"
                          style={{ color: 'var(--color-accent-danger)' }}
                        >
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : accounts.length === 0 ? (
          <div className="empty-state">
            <BanknotesIcon className="empty-state-icon" />
            <p className="empty-state-title">No accounts found</p>
            <p>Add your first account to get started</p>
            <button className="btn btn-primary" onClick={() => openModal()} style={{ marginTop: '1rem' }}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Add Account
            </button>
          </div>
        ) : (
          <div className="empty-state">
            <MagnifyingGlassIcon className="empty-state-icon" />
            <p className="empty-state-title">No accounts found</p>
            <p>No accounts match your search criteria</p>
            <button className="btn btn-secondary" onClick={() => setSearch('')} style={{ marginTop: '1rem' }}>
              Clear Search
            </button>
          </div>
        )}
      </div>

      {/* Account Form Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingAccount ? 'Edit Account' : 'Add New Account'}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editingAccount ? 'Update Account' : 'Create Account'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Account Number *</label>
              <input
                type="text"
                className="form-input"
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                placeholder="e.g., 1000, 2100"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Account Type *</label>
              <select
                className="form-select"
                value={formData.account_type}
                onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
              >
                <option value="">Select type</option>
                {ACCOUNT_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Account Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter account name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Parent Account</label>
            <select
              className="form-select"
              value={formData.parent_account_id}
              onChange={(e) => setFormData({ ...formData, parent_account_id: e.target.value })}
            >
              <option value="">None (Top Level)</option>
              {getAvailableParents().map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.account_number} - {acc.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter account description (optional)"
              rows={4}
              maxLength={500}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-xs)' }}>
              {formData.description.length}/500 characters
            </div>
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingBottom: '0.5rem' }}>
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <span>Active</span>
            </label>
          </div>
        </form>
      </Modal>

      {/* View Account Modal */}
      <Modal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        title="Account Details"
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeViewModal}>Close</button>
            <button className="btn btn-primary" onClick={() => { closeViewModal(); openModal(viewingAccount); }}>
              Edit Account
            </button>
          </>
        }
      >
        {viewingAccount && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Account Number</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  <strong>{viewingAccount.account_number}</strong>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Account Type</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  <span className={`badge badge-${viewingAccount.account_type?.toLowerCase()}`}>
                    {viewingAccount.account_type}
                  </span>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Account Name</label>
              <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                {viewingAccount.name}
              </div>
            </div>

            {viewingAccount.description && (
              <div className="form-group">
                <label className="form-label">Description</label>
                <div style={{ padding: 'var(--space-md)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', whiteSpace: 'pre-wrap' }}>
                  {viewingAccount.description}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Parent Account</label>
              <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                {viewingAccount.parent_account_number ? (
                  `${viewingAccount.parent_account_number} - ${viewingAccount.parent_account_name}`
                ) : '-'}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                {viewingAccount.is_active ? (
                  <span className="badge badge-approved">Active</span>
                ) : (
                  <span className="badge badge-draft">Inactive</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Created At</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }}>
                  {new Date(viewingAccount.created_at).toLocaleString()}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Last Updated</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }}>
                  {viewingAccount.updated_at ? new Date(viewingAccount.updated_at).toLocaleString() : '-'}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ChartOfAccounts;
